import type { ConnectionStatus, PeerAudioState, PlayerPosition } from '../types';

interface StatusDisplayProps {
  wsStatus: ConnectionStatus;
  roomStatus: string;
  peerAudioStates: PeerAudioState[];
  myPosition: PlayerPosition | null;
  error: string | null;
}

/**
 * Displays connection status and peer information
 */
export function StatusDisplay({
  wsStatus,
  roomStatus,
  peerAudioStates,
  myPosition,
  error,
}: StatusDisplayProps) {
  return (
    <div className="status-display">
      {/* Connection Status */}
      <div className="status-section">
        <h3>Connection Status</h3>
        <div className="status-row">
          <StatusIndicator status={wsStatus} label="Game Server" />
          <StatusIndicator status={roomStatus === 'connected' ? 'connected' : 'disconnected'} label="Voice Room" />
        </div>
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
      </div>

      {/* Your Position */}
      {myPosition && (
        <div className="status-section">
          <h3>Your Position</h3>
          <div className="position-info">
            <span>X: {myPosition.x.toFixed(1)}</span>
            <span>Y: {myPosition.y.toFixed(1)}</span>
            <span>Z: {myPosition.z.toFixed(1)}</span>
          </div>
        </div>
      )}

      {/* Nearby Players */}
      <div className="status-section">
        <h3>Nearby Players ({peerAudioStates.length})</h3>
        {peerAudioStates.length === 0 ? (
          <div className="no-peers">No players nearby</div>
        ) : (
          <div className="peer-list">
            {peerAudioStates.map((peer) => (
              <PeerStatus key={peer.peerId} peer={peer} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface StatusIndicatorProps {
  status: ConnectionStatus | 'connected' | 'disconnected';
  label: string;
}

function StatusIndicator({ status, label }: StatusIndicatorProps) {
  const getStatusColor = () => {
    switch (status) {
      case 'connected':
        return 'status-green';
      case 'connecting':
        return 'status-yellow';
      case 'disconnected':
        return 'status-gray';
      case 'error':
        return 'status-red';
      default:
        return 'status-gray';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return 'Connecting...';
      case 'disconnected':
        return 'Disconnected';
      case 'error':
        return 'Error';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="status-indicator">
      <div className={`status-dot ${getStatusColor()}`} />
      <div className="status-text">
        <span className="status-label">{label}</span>
        <span className="status-value">{getStatusText()}</span>
      </div>
    </div>
  );
}

interface PeerStatusProps {
  peer: PeerAudioState;
}

function PeerStatus({ peer }: PeerStatusProps) {
  const volumePercent = Math.round(peer.volume * 100);
  const distanceText = peer.distance.toFixed(1);

  return (
    <div className={`peer-status ${peer.muted ? 'muted' : ''}`}>
      <div className="peer-info">
        <span className="peer-name">
          {peer.muted && <MutedIcon />}
          {peer.connected && <ConnectedIcon />}
          {peer.name}
        </span>
        <span className="peer-distance">{distanceText}m</span>
      </div>
      <div className="volume-bar-container">
        <div 
          className="volume-bar" 
          style={{ width: `${volumePercent}%` }}
        />
      </div>
      <span className="volume-text">{volumePercent}%</span>
    </div>
  );
}

function MutedIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" className="muted-icon">
      <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
    </svg>
  );
}

function ConnectedIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" className="connected-icon">
      <path d="M9 16.2 5.5 12.7 4.1 14.1 9 19 20.3 7.7 18.9 6.3z" />
    </svg>
  );
}
